import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Input, Switch, Typography } from "antd";
import axios from "axios";
import { token, url } from "../../Functionalities/Config";
import { showMessage } from "../../Functionalities/Functions";
import { mobilesText, staticOtpError, staticOtpUrl } from "./staticOtp";

const notify = (text, type) =>
  showMessage(text, type, type === "success" ? "Success!" : "Error!", "top", "top-right", 0);

const StaticOtpCard = () => {
  const [enabled, setEnabled] = useState(false);
  const [anyMobile, setAnyMobile] = useState(false);
  const [mobiles, setMobiles] = useState("");
  const [anyMobileAllowed, setAnyMobileAllowed] = useState(false);
  const [code, setCode] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  const apply = (setting) => {
    setEnabled(Boolean(setting.enabled));
    setAnyMobile(Boolean(setting.any_mobile));
    setMobiles(mobilesText(setting.mobiles));
    setAnyMobileAllowed(Boolean(setting.any_mobile_allowed));
    setCode(setting.code || "");
    setUpdatedAt(setting.updated_at);
  };

  useEffect(() => {
    axios
      .get(staticOtpUrl(url), { headers: { Authorization: token } })
      .then((response) => {
        if (response.data?.Status) apply(response.data.Message);
      })
      .catch(() => {
        // The card stays empty; saving still works and reports its own error.
      });
  }, []);

  const save = () => {
    const error = staticOtpError({ enabled, anyMobile, mobiles });
    if (error) {
      notify(error, "danger");
      return;
    }
    const data = new FormData();
    data.append("enabled", enabled ? "true" : "false");
    data.append("any_mobile", anyMobile && anyMobileAllowed ? "true" : "false");
    data.append("mobiles", mobiles);

    setSaving(true);
    axios
      .put(staticOtpUrl(url), data, { headers: { Authorization: token } })
      .then((response) => {
        if (response.data?.Status) {
          apply(response.data.Message);
          notify(response.data.Message.enabled ? "Static OTP is on" : "Static OTP is off", "success");
        } else {
          notify(response.data?.Error || "Could not save the static OTP", "danger");
        }
      })
      .catch((err) => notify(err?.response?.data?.Error || "Could not save the static OTP", "danger"))
      .finally(() => setSaving(false));
  };

  return (
    <Card
      size="small"
      title="Static OTP"
      style={{ margin: "0 0 16px" }}
      extra={
        <Switch
          checked={enabled}
          onChange={setEnabled}
          checkedChildren="On"
          unCheckedChildren="Off"
          aria-label="Turn the static OTP on"
        />
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
        While it is on, these numbers log in with code <Typography.Text strong>{code}</Typography.Text> and no SMS
        is sent. Staff and admin accounts never log in with it.
      </Typography.Paragraph>
      <Input.TextArea
        value={mobiles}
        onChange={(event) => setMobiles(event.target.value)}
        autoSize={{ minRows: 2, maxRows: 6 }}
        placeholder={"9876543210\n9123456780"}
        aria-label="Test mobile numbers, one per line"
      />
      {anyMobileAllowed ? (
        <div style={{ marginTop: 12 }}>
          <Switch
            checked={anyMobile}
            onChange={setAnyMobile}
            size="small"
            aria-label="Accept the code for every number"
          />{" "}
          <Typography.Text>Every number (this test server only)</Typography.Text>
          {anyMobile && enabled && (
            <Alert
              style={{ marginTop: 8 }}
              type="warning"
              showIcon
              message={`Anyone can log in as any customer with ${code} while this is on.`}
            />
          )}
        </div>
      ) : (
        <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
          This server accepts the code only for the numbers listed above.
        </Typography.Paragraph>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
        <Button type="primary" onClick={save} loading={saving}>
          Save
        </Button>
        {updatedAt && (
          <Typography.Text type="secondary">Last saved {new Date(updatedAt).toLocaleString()}</Typography.Text>
        )}
      </div>
    </Card>
  );
};

export default StaticOtpCard;
